import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  Calendar,
  Search,
  ArrowRight,
  MapPin,
  Mic,
  Globe,
  ChevronLeft,
  ChevronRight,
  ChevronLast,
  ChevronFirst,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { useCollection } from "../hooks/useCollection";
import { useNavigate } from "react-router-dom";

/**
 * Portail Public des Conférences CEISNA
 * - Intégration Firestore via useCollection
 * - Pagination intelligente (6 par page)
 * - Multi-sélection d'années
 */
const Conferences = () => {
  const scrollRef = useRef(null);
  const ITEMS_PER_PAGE = 6;
  const navigate = useNavigate();

  // Récupération des données réelles
  const {
    documents: dbConferences,
    loading,
    error,
  } = useCollection("vonference");

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedYears, setSelectedYears] = useState(["Toutes"]);
  const [currentPage, setCurrentPage] = useState(1);

  // Années dynamiques de 2023 à 2030 pour le filtre horizontal
  const filterYears = useMemo(() => {
    const list = ["Toutes"];
    for (let i = 2030; i >= 2023; i--) list.push(i.toString());
    return list;
  }, []);

  // --- LOGIQUE DE FILTRAGE SUR LES DONNÉES DB ---
  const filteredConferences = useMemo(() => {
    if (!dbConferences) return [];

    return dbConferences
      .filter((c) => {
        const title = c.hero?.title || "";
        const location = c.hero?.location || "";
        const year = c.hero?.year?.toString() || "";

        const matchesSearch =
          title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          location.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesYear =
          selectedYears.includes("Toutes") || selectedYears.includes(year);

        return matchesSearch && matchesYear;
      })
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)); // Tri par ajout récent
  }, [dbConferences, searchTerm, selectedYears]);

  // --- LOGIQUE DE PAGINATION ---
  const totalPages = Math.ceil(filteredConferences.length / ITEMS_PER_PAGE);
  const currentData = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredConferences.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredConferences, currentPage]);

  // Reset pagination lors d'un changement de filtre
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedYears]);

  // --- ACTIONS ---
  const scroll = (direction) => {
    if (scrollRef.current) {
      const amount = 300;
      scrollRef.current.scrollBy({
        left: direction === "left" ? -amount : amount,
        behavior: "smooth",
      });
    }
  };

  const toggleYear = (year) => {
    const yStr = year.toString();
    if (yStr === "Toutes") {
      setSelectedYears(["Toutes"]);
      return;
    }
    setSelectedYears((prev) => {
      const next = prev.filter((y) => y !== "Toutes");
      if (next.includes(yStr)) {
        const res = next.filter((y) => y !== yStr);
        return res.length === 0 ? ["Toutes"] : res;
      }
      return [...next, yStr];
    });
  };

  return (
    <main className="bg-[#FDFDFF] font-sans text-slate-900 selection:bg-indigo-100 selection:text-indigo-900 pb-32">
      {/* HERO SECTION */}
      <header className="px-6 lg:px-16 pt-24 pb-16 bg-white">
        <div className="max-w-4xl animate-in fade-in slide-in-from-bottom-4 duration-700">
          <h2 className="text-sm font-black text-indigo-600 uppercase tracking-[0.3em] mb-4">
            Archives & Futur
          </h2>
          <h1 className="text-4xl lg:text-7xl font-black tracking-tighter text-indigo-950 uppercase leading-[0.9]">
            Explorez les Éditions <br />{" "}
            <span className="text-slate-300">des Conférences CEISNA</span>
          </h1>
          <p className="text-lg lg:text-xl text-slate-500 font-medium mt-8 max-w-2xl leading-relaxed">
            Consultez notre historique et nos projets futurs pour la
            transformation de la santé numérique en Afrique.
          </p>
        </div>
      </header>

      {/* RECHERCHE ET FILTRES DATES */}
      <section className="px-6 lg:px-16 mb-16 -mt-8 relative z-10">
        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-6 p-2 bg-white rounded-[2.5rem] shadow-2xl shadow-slate-200/60 border border-slate-50 overflow-hidden">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className="pl-6 text-slate-300 shrink-0">
              <Search size={22} />
            </div>
            <input
              type="text"
              placeholder="Rechercher une conférence..."
              className="w-full p-4 outline-none font-bold text-slate-700 placeholder:text-slate-300 min-w-0"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="flex items-center bg-slate-50 rounded-[1.8rem] p-1 relative flex-1 md:flex-initial max-w-full md:max-w-md lg:max-w-lg overflow-hidden ">
            <button
              onClick={() => scroll("left")}
              className="flex shrink-0 z-10 w-10 h-10 bg-white rounded-full items-center justify-center shadow-md border border-slate-100 text-indigo-600 hover:bg-indigo-600 hover:text-white transition-all active:scale-90"
            >
              <ChevronLeft size={20} />
            </button>

            <div
              ref={scrollRef}
              className="flex items-center gap-2 overflow-x-auto no-scrollbar scroll-smooth px-3 flex-1"
            >
              {filterYears.map((year) => (
                <button
                  key={year}
                  onClick={() => toggleYear(year)}
                  className={`whitespace-nowrap px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all duration-300 shrink-0 ${
                    selectedYears.includes(year.toString())
                      ? "bg-white text-indigo-600 shadow-sm scale-105"
                      : "text-slate-400 hover:text-slate-600 hover:bg-white/50"
                  }`}
                >
                  {year}
                </button>
              ))}
            </div>

            <button
              onClick={() => scroll("right")}
              className="flex shrink-0 z-10 w-10 h-10 bg-white rounded-full items-center justify-center shadow-md border border-slate-100 text-indigo-600 hover:bg-indigo-600 hover:text-white transition-all active:scale-90"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* CONTENT AREA */}
      <main className="px-6 lg:px-16">
        {loading ? (
          <div className="py-40 flex flex-col items-center justify-center text-indigo-600 gap-4">
            <Loader2 size={48} className="animate-spin" />
            <p className="font-black uppercase text-xs tracking-widest animate-pulse">
              Synchronisation...
            </p>
          </div>
        ) : error ? (
          <div className="py-32 text-center text-red-500 flex flex-col items-center gap-4">
            <AlertCircle size={48} />
            <p className="font-black uppercase tracking-widest">{error}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-12 mb-20">
              {currentData.map((conf) => {
                const spkCount =
                  (conf.speakers1?.length || 0) +
                  (conf.speakers2?.length || 0) +
                  (conf.speakers3?.length || 0);

                return (
                  <article
                    key={conf.id}
                    className="bg-white rounded-[3.5rem] overflow-hidden shadow-sm hover:shadow-2xl hover:shadow-indigo-100 transition-all duration-500 flex flex-col border border-slate-50 "
                  >
                    <div className="relative h-72 overflow-hidden bg-slate-100">
                      <img
                        src={
                          conf.hero?.bgImage ||
                          "https://images.unsplash.com/photo-1551601651-2a8555f1a136"
                        }
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        alt={conf.hero?.title}
                      />
                      <div className="absolute top-8 left-8">
                        <span className="bg-white/90 backdrop-blur-md px-6 py-2 rounded-2xl text-[10px] font-black uppercase tracking-widest text-indigo-950 shadow-sm">
                          {conf.hero?.year || "N/A"}
                        </span>
                      </div>
                    </div>

                    <div className="p-12 flex-1 flex flex-col">
                      <div className="mb-8">
                        <h3 className="text-2xl font-black text-indigo-950 leading-[1.1] uppercase tracking-tighter mb-4 italic line-clamp-2">
                          {conf.hero?.title?.split("\n").join(" ")}
                        </h3>
                        <p className="text-slate-500 text-sm font-medium leading-relaxed italic border-l-4 border-indigo-100 pl-6 line-clamp-3">
                          {conf.hero?.description}
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-8 mb-10">
                        <div className="space-y-1">
                          <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest block">
                            Lieu
                          </span>
                          <div className="flex items-center gap-2 font-bold text-slate-700 text-sm truncate">
                            <MapPin
                              size={16}
                              className="text-indigo-400 shrink-0"
                            />
                            {conf.hero?.location}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest block">
                            Experts
                          </span>
                          <div className="flex items-center gap-2 font-bold text-slate-700 text-sm">
                            <Mic
                              size={16}
                              className="text-indigo-400 shrink-0"
                            />
                            {spkCount} Intervenants
                          </div>
                        </div>
                      </div>

                      <div className="mt-auto pt-8 border-t border-slate-50 flex items-center justify-between">
                        <div className="flex items-center gap-2 text-indigo-600 font-black text-[10px] uppercase tracking-widest">
                          <Calendar size={16} />
                          {conf.hero?.date}
                        </div>
                        <button
                          onClick={() => navigate(`/conference/${conf.id}`)}
                          className="flex items-center gap-2 bg-indigo-50 text-indigo-600 px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] hover:bg-indigo-600 hover:text-white transition-all"
                        >
                          Détails <ChevronRight size={14} />
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>

            {/* PAGINATION MODERNE */}
            {totalPages > 1 && (
              <div className="flex flex-col items-center gap-6">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="w-12 h-12 flex items-center justify-center rounded-2xl border bg-white text-slate-400 disabled:opacity-20 hover:bg-slate-50 transition-colors shadow-sm"
                  >
                    <ChevronFirst size={20} />
                  </button>
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="w-12 h-12 flex items-center justify-center rounded-2xl border bg-white text-slate-400 disabled:opacity-20 hover:bg-slate-50 transition-colors shadow-sm"
                  >
                    <ChevronLeft size={20} />
                  </button>

                  <div className="flex items-center gap-2 bg-white p-1 rounded-[1.5rem] border border-slate-100 shadow-sm">
                    {[...Array(totalPages)].map((_, i) => {
                      const page = i + 1;
                      if (
                        page === 1 ||
                        page === totalPages ||
                        Math.abs(page - currentPage) <= 1
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`w-10 h-10 rounded-xl text-[10px] font-black transition-all ${
                              currentPage === page
                                ? "bg-indigo-600 text-white shadow-lg shadow-indigo-100 scale-110"
                                : "text-slate-400 hover:text-indigo-600"
                            }`}
                          >
                            {page}
                          </button>
                        );
                      }
                      if (page === 2 || page === totalPages - 1)
                        return (
                          <span key={page} className="px-1 text-slate-300">
                            ...
                          </span>
                        );
                      return null;
                    })}
                  </div>

                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="w-12 h-12 flex items-center justify-center rounded-2xl border bg-white text-slate-400 disabled:opacity-20 hover:bg-slate-50 transition-colors shadow-sm"
                  >
                    <ChevronRight size={20} />
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="w-12 h-12 flex items-center justify-center rounded-2xl border bg-white text-slate-400 disabled:opacity-20 hover:bg-slate-50 transition-colors shadow-sm"
                  >
                    <ChevronLast size={20} />
                  </button>
                </div>
                <p className="text-[10px] font-black uppercase text-slate-300 tracking-widest">
                  Page {currentPage} sur {totalPages} —{" "}
                  {filteredConferences.length} Résultats
                </p>
              </div>
            )}
          </>
        )}

        {!loading && filteredConferences.length === 0 && (
          <div className="py-32 text-center opacity-30">
            <Globe size={80} className="mx-auto mb-6 text-slate-400" />
            <p className="text-2xl font-black uppercase tracking-widest">
              Aucune édition trouvée
            </p>
          </div>
        )}
      </main>

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </main>
  );
};

export default Conferences;
